export const REFRESH_SOURCE_STATES = ['ok', 'late', 'held_by_checks', 'held_by_operator', 'quarantined'] as const;

/** `unknown` is a state a newer job writes that this Studio does not know. */
export type RefreshSourceState = (typeof REFRESH_SOURCE_STATES)[number] | 'unknown';

export type RefreshNote = { by: string; note: string; at: string };

export type RefreshLanding = { snapshotId: string; committedAt: string };

export type RefreshRestorePoint = RefreshLanding & { liveRows: number };

export type RefreshThinnedCount = { name: string; count: number; floor: number | null };

export type RefreshSource = {
  table: string;
  state: RefreshSourceState;
  since: string;
  cause: string | null;
  /** The landing segments read. */
  accepted: RefreshLanding | null;
  /** The restore points, newest first. */
  earlierAccepted: RefreshRestorePoint[];
  /** The newest landing the run judged and did not accept: what Accept names. */
  held: RefreshLanding | null;
  /** Lists or keys below their floor: what an accept's counts may name. */
  thinned: RefreshThinnedCount[];
  accept: string | null;
  quarantineReaders: { count: number; segmentIds: string[] } | null;
};

export type RefreshImage = { image: string; copies: number };

export type RefreshFailingSegment = { segmentId: string; errorCode: string; failures: number; since: string };

export type RefreshStatus = {
  writtenAt: string;
  image: string | null;
  tables: string[] | null;
  controlsUnreadable: { reason: string; at: string } | null;
  rebuilt: { at: string; reason: string; tablesRestored: boolean } | null;
  images: RefreshImage[] | null;
  reseedsPending: { asOf: string; segmentIds: string[]; segmentsTotal: number; images: RefreshImage[] } | null;
  sources: RefreshSource[];
  failing: RefreshFailingSegment[];
  failingTotal: number;
};

export type RefreshControl = {
  table: string;
  hold: RefreshNote | null;
  accept: (RefreshNote & { snapshotId: string; counts: string[] }) | null;
  quarantine: (RefreshNote & { landings: RefreshLanding[]; restore: RefreshRestorePoint }) | null;
};

export type RefreshControls = {
  /** What a write over the controls sends back; null when there are none yet. */
  version: string | null;
  readable: boolean;
  unreadable: { reason: string; tables: { table: string; reads: boolean }[] | null; clearAllOnly: boolean } | null;
  tables: RefreshControl[];
};

export type RefreshReseeds = {
  version: string | null;
  readable: boolean;
  unreadableReason: string | null;
  segments: (RefreshNote & { segmentId: string })[];
  images: (RefreshNote & { image: string })[];
};

/** The Refresh health page as one read answers it. */
export type RefreshPage = {
  /** Null before the job's first run. */
  status: RefreshStatus | null;
  controls: RefreshControls;
  reseeds: RefreshReseeds;
};

type ActionNote = {
  /** One line of at most 500 characters. Required by every act but a release and a lift of a quarantine. */
  note?: string;
};

export type RefreshAction = ActionNote &
  (
    | { kind: 'hold'; table: string; version: string | null }
    | { kind: 'release'; table: string; version: string | null }
    | { kind: 'accept'; table: string; version: string | null; snapshotId: string; counts?: string[] }
    | { kind: 'quarantine'; table: string; version: string | null; snapshotId: string }
    | { kind: 'lift_quarantine'; table: string; version: string | null }
    | { kind: 'clear'; table: string; version: string | null }
    | { kind: 'clear_all'; version: string | null }
    | { kind: 'reseed_segment'; segmentId: string }
    | { kind: 'reseed_image'; image: string }
    | { kind: 'clear_reseeds'; version: string | null }
  );

export type RefreshActionKind = RefreshAction['kind'];
